/**
 * UI Interaction Logger
 *
 * Logs all button clicks and menu actions to backend for correlation with server logs
 */

import { DATABASE_SERVICE_URL } from './workerUrls';

class UILogger {
  private queue: Array<{ action: string; component: string; details?: Record<string, unknown> }> = [];
  private flushTimer: number | null = null;
  private flushInterval = 2000; // Batch requests every 2 seconds

  /**
   * Log a UI interaction (button click, menu action, etc.)
   */
  log(component: string, action: string, details?: Record<string, unknown>): void {
    this.queue.push({ action, component, details });

    // Schedule flush if not already scheduled
    if (!this.flushTimer) {
      this.flushTimer = window.setTimeout(() => this.flush(), this.flushInterval);
    }
  }

  /**
   * Log a button click
   */
  button(component: string, buttonName: string, details?: Record<string, unknown>): void {
    this.log(component, `button_click_${buttonName}`, details);
  }

  /**
   * Log a menu action
   */
  menu(component: string, menuItem: string, details?: Record<string, unknown>): void {
    this.log(component, `menu_${menuItem}`, details);
  }

  /**
   * Log a panel open/close
   */
  panel(panelName: string, action: 'open' | 'close', details?: Record<string, unknown>): void {
    this.log('Console', `panel_${action}_${panelName}`, details);
  }

  /**
   * Flush pending logs to backend
   */
  private async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const batch = [...this.queue];
    this.queue = [];
    this.flushTimer = null;

    try {
      const databaseUrl = DATABASE_SERVICE_URL();

      // Send all logs in batch
      await Promise.all(
        batch.map(async (log) => {
          try {
            await fetch(`${databaseUrl}/api/ui-logs`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(log),
            });
          } catch (error) {
            console.error('Failed to log UI interaction:', error);
          }
        })
      );
    } catch (error) {
      console.error('Failed to flush UI logs:', error);
    }
  }

  /**
   * Immediately flush all pending logs (for page unload)
   */
  async flushNow(): Promise<void> {
    if (this.flushTimer) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    await this.flush();
  }
}

// Singleton instance
export const uiLogger = new UILogger();

// Flush logs before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    uiLogger.flushNow();
  });
}

export default uiLogger;
